import React, { useState, useEffect } from 'react';
import TransitionLink from 'gatsby-plugin-transition-link';
import { TimelineMax, Power1 } from 'gsap';
import convert from 'color-convert';

/* eslint-disable no-undef */
/* eslint-disable class-methods-use-this */
/* eslint-disable react/jsx-props-no-spreading */

// This file is a heavily modified version of https://github.com/TylerBarnes/gatsby-plugin-transition-link/blob/master/src/AniLink/PaintDrip.js

class Ripple {
  constructor(props, ripple) {
    this.props = props;
    this.ripple = ripple;
  }

  draw() {
    // Get canvas & context
    const canvas = this.getCanvas();
    const context = this.getContext();

    // Get props
    const { hex } = this.props;

    // Get ripple state
    const {
      x,
      y,
      radius,
      alpha,
    } = this.ripple;

    // Drawing circle path
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2, false);

    // Convert HEX color to RGB if available
    let rgb = '0,0,255';

    if (hex) {
      rgb = convert.hex.rgb(hex).join(',');
    }

    // Fill path with color
    context.fillStyle = `rgba(${rgb},${alpha})`;
    context.fill();

    return this;
  }

  start() {
    // Append canvas to body
    document.body.appendChild(this.getCanvas());

    // Get props
    const {
      ease,
      radius,
      duration,
      startNode,
    } = this.props;

    // Start timeline
    this.getTimeline()
      .to(this.ripple, { // Fade in ripple for 1/3 of duration
        alpha: 1,
        duration: duration / 3,
      })
      .to(this.ripple, { // Expand ripple to target radius for full duration starting at same time as fade
        radius,
        duration,
        ease: ease || Power1.easeIn,
      }, 0)
      .addLabel('reverse-start') // Place a label for the reverse to start at
      .set(startNode, { // Hide the original page
        visibility: 'hidden'
      })
      .addPause(); // Pause the ripple

    return this;
  }

  end() {
    // End timeline
    this.getTimeline()
      .reverse('reverse-start'); // Reverse from reverse-start label

    return this;
  }

  remove() {
    // Remove canvas from body
    document.body.removeChild(this.getCanvas());

    return this;
  }

  getCanvas() {
    // Create canvas if does not exist yet
    if (typeof this.canvas === 'undefined') {
      // Create canvas element
      const canvas = document.createElement('canvas');

      // Set canvas positioning styles
      canvas.style.zIndex = 10000;
      canvas.style.top = 0;
      canvas.style.left = 0;
      canvas.style.position = 'fixed';

      // Set canvas dimensions
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // Save canvas
      this.canvas = canvas;
    }

    return this.canvas;
  }

  getContext() {
    return this.getCanvas().getContext('2d');
  }

  getTimeline() {
    // Create timeline if does not exist yet
    if (typeof this.timeline === 'undefined') {
      this.timeline = new TimelineMax({
        onUpdate: () => this.draw(),
        onReverseComplete: () => this.remove(),
      });
    }

    return this.timeline;
  }

  setProps(props) {
    if (props && props === Object(props)) {
      this.props = {
        ...this.props,
        ...props,
      };
    }

    return this;
  }

  setRipple(ripple) {
    if (ripple && ripple === Object(ripple)) {
      this.ripple = {
        ...this.ripple,
        ...ripple,
      };
    }

    return this;
  }
}

const TransitionLinkCustom = ({
  children,
  hex,
  ease,
  duration,
  ...props
}) => {
  // Set ripple in state
  const [ripple, setRipple] = useState(null);

  useEffect(() => {
    const initProps = {
      hex,
      ease,
      duration,
    };

    const initRipple = {
      alpha: 0,
      radius: 0,
      x: 0,
      y: 0,
    };

    setRipple(new Ripple(initProps, initRipple));
  }, [hex, duration]);

  // Define exit function
  const exitRipple = (event, node) => {
    // Don't run if not in browser
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Get event coords
    const x = event.clientX;
    const y = event.clientY;

    // Get viewport dimensions
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Get delta - difference between event coords and farthest corner
    const dx = x < vw / 2 ? vw - x : x;
    const dy = y < vh / 2 ? vh - y : y;

    const radius = Math.sqrt(dx * dx + dy * dy);

    // Set props & ripple coords, start animation
    ripple
      .setProps({
        radius,
        startNode: node,
      })
      .setRipple({
        x,
        y,
      })
      .start();
  };

  // Define enter function
  const enterRipple = (node) => {
    // Don't run if not in browser
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // End ripple
    ripple
      .setProps({
        endNode: node,
      })
      .end();
  };

  return (
    <TransitionLink
      exit={{
        length: duration,
        trigger: ({ e, node }) => exitRipple(e, node),
      }}
      entry={{
        delay: duration + 0.25,
        length: duration,
        trigger: ({ node }) => enterRipple(node)
      }}
      {...props}
    >
      {children}
    </TransitionLink>
  );
};

const LinkTransition = ({
  to,
  className,
  activeClassName,
  children
}) => (
  <TransitionLinkCustom
    to={to}
    duration={0.75}
    hex="#FF0F55"
    className={className}
    activeClassName={activeClassName}
    partiallyActive
  >
    {children}
  </TransitionLinkCustom>
);

export default LinkTransition;
